import * as React from 'react';
import { connect } from 'react-redux';
import { flow, map, forEach, has } from 'lodash';
import { Col, Row, OverlayTrigger, Popover, Button, Modal } from 'react-bootstrap';
import { II18nMessages } from '../../../../services/branding/models';
import { IDestination, IPolicyApplication, IPremium, IProduct, IQuotes, ITraveller } from '../../../../data/api/models';
import { formValueSelector, change } from 'redux-form';
import { IStore } from '../../../../redux/IStore';
import { FormattedNumber } from 'react-intl';
import { ICompleteData, IQuoteActions } from '../../../../services/quote/models';
import { FaInfoCircle } from 'react-icons/fa';
import { showDestinations, showRegions } from '../../../../data/api/api';
import { bindActionCreators } from 'redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';

interface IProps {
  i18n: II18nMessages;
  policyApplication: IPolicyApplication;
  destinations: IDestination[];
  values: IPolicyApplication;
  product: IProduct;
  quotes: IQuotes;
  complete: ICompleteData;
  actions: {
    quote: IQuoteActions;
  };
  channel: any;
}

class QuoteSummary extends React.Component<IProps, any> {
  public state = {
    showSaveQuoteModal: false,
  };

  private renderTripType() {
    const { i18n, values, product } = this.props;

    return (
      <div className="section">
        {i18n.quote.summary.tripType}
        <div className="quote-info">
          <p>{product.metadata.trip_type[values.tripType]}</p>
        </div>
      </div>
    );
  }

  private renderTravelDate() {
    const { values } = this.props;

    let content = (
      <p>No dates entered</p>
    );

    if (values.startDate) {
      content = (
        <p>
          {values.startDate}
          {values.tripType === 'single' && values.endDate ? ' - ' + values.endDate : ''}
        </p>
      );
    }

    return (
      <div className="section">
        {values.tripType === 'single' ? 'Travel Dates' : 'Start Date'}
        <div className="quote-info">
          {content}
        </div>
      </div>
    );
  }

  private renderDestination() {
    const { values, product } = this.props;

    let content = (
      <p>Nothing Selected</p>
    );

    if (showDestinations(values.tripType) && values.destinations
    ) {
      content = (
        <div>
          {values.destinations.map((destination, key) => (<p key={key}>{destination}</p>))}
        </div>
      );
    }

    if (showRegions(values.tripType) && values.region) {
      content = (
        <p>{product.metadata.regions[values.region]}</p>
      );
    }

    return (
      <div className="section">
        {showDestinations(values.tripType) ?
          (values.destinations && values.destinations.length > 1 ? 'Destinations' : 'Destination') : 'Region'}
        <div className="quote-info">
          {content}
        </div>
      </div>
    );
  }

  private renderAdditionalOptions = () => {
    const { values, product } = this.props;

    const options = map(values.options, (value: any, key) => {
      const productOption = product.metadata.options[key];

      if (productOption.type === 'boolean' && value === true) {
        return (
          <p key={key}>{productOption.name}</p>
        );
      }

      if (productOption.type === 'list') {
        const item = productOption.items[value];

        const popover = (
          <Popover
            id={`popover-summary-${name}`}
          >
            {item}
          </Popover>
        );

        const info = (
          <OverlayTrigger
            trigger="click"
            rootClose={true}
            overlay={popover}
            placement="right"
            positionLeft={200}
            positionTop={50}
          >
            <FaInfoCircle className="pointer"/>
          </OverlayTrigger>
        );

        return (
          <p key={key}>
            {productOption.name} {info}
          </p>
        );
      }
    }).filter((option) => !!option);

    if (options.length === 0) {
      return null;
    }

    // const renderOptions = options.map((option, key) => {
    //   return (<p key={key}>{option}</p>);
    // });

    return (
      <div className="section">
        Additional {options.length > 1 ? 'Options' : 'Option'}
        <div className="quote-info">
          {options}
        </div>
      </div>
    );
  }

  private renderActivities = () => {
    const { values } = this.props;

    if (!values.activities) {
      return null;
    }

    return (
      <div className="section">
        {values.activities.length > 1 ? 'Activities' : 'Activity'}
        <div className="quote-info">
          {values.activities.map((item, key) => {
            return <p key={key}>{item}</p>;
          })}
        </div>
      </div>
    );
  }

  private getSchemeDocuments = () => {
    const { channel: { products } } = this.props;

    const schemeDocuments = {};
    products.forEach((product) => {
      product.schemeDocuments.forEach((schemeDocument) => {
        schemeDocuments[schemeDocument.attributes.short_name] = schemeDocument.attributes;
      });
    });

    return schemeDocuments;
  }

  private renderSchemeDocument(documentName) {
    const schemeDocuments = this.getSchemeDocuments();

    if (has(schemeDocuments, documentName)) {
      return (
        <div className="section">
          <a
            href={schemeDocuments[documentName].file_path}
            className="btn btn-primary btn-block policy-docs"
            target="_blank"
          >
            {schemeDocuments[documentName].short_name}
          </a>
        </div>
      );
    } else {
      return (
        <div className="section">
          <a href="" className="btn btn-primary btn-block policy-docs" target="_blank">
            {documentName}
          </a>
        </div>
      );
    }
  }

  private renderProductDocument(documentName, displayName) {
    const documents = this.props.channel.products.length > 0 ? this.props.channel.products[0].documents : {};

    if (has(documents, documentName)) {
      return (
        <div className="section">
          <a
            href={documents[documentName]}
            className="btn btn-primary btn-block policy-docs"
            target="_blank"
          >
            {displayName}
          </a>
        </div>
      );
    } else {
      return (
        <div className="section">
          <a className="btn btn-primary btn-block policy-docs" target="_blank">
            {displayName}
          </a>
        </div>
      );
    }
  }

  private renderTraveller = (type: string, display: string) => (traveller: ITraveller, key) => {
    let conditions = null;

    if (traveller.screening) {
      conditions = (
        <span className="traveller-conditions"><br/>{traveller.screening.meta.conditions.join(', ')}</span>
      );
    }

    return (
      <p key={key}>
        {display} {key + 1}: <em>{traveller.title} {traveller.firstName} {traveller.lastName}</em> ({traveller.dob})
        {conditions}
      </p>
    );
  }

  private renderTravellers = () => {
    const { values, product } = this.props;
    const { travellers } = values;

    if (!travellers || !values.tripType) {
      return null;
    }

    const adults = travellers.filter((traveller) => traveller.type === 'adult');
    const children = travellers.filter((traveller) => traveller.type === 'child');
    const groupTypes = product.metadata[`group_type_trip_${values.tripType}`];

    const total = adults.length + children.length;

    let summaryMessage = `${adults.length} ${adults.length > 1 ? 'adults' : 'adult'}`;

    if (children.length) {
      summaryMessage += `, ${children.length} ${children.length > 1 ? 'children' : 'child'}`;
    }

    return (
      <div className="section">
        {total > 1 ? 'Travellers' : 'Traveller'}
        <div className="quote-info">
          <p>{groupTypes?.[values.groupType]} ({summaryMessage})</p>

          {adults.map(this.renderTraveller('adult', 'Adult'))}
          {children.map(this.renderTraveller('child', 'Child'))}
        </div>
      </div>
    );
  }

  private renderPremium = () => {
    const { quotes, values: { schemeId }, product } = this.props;

    if (!quotes) {
      return null;
    }

    let premium: IPremium = null;

    forEach(quotes, (tripTypes) => {
      forEach(tripTypes, (quotedPremium) => {
        if (quotedPremium.scheme.id === schemeId) {
          premium = quotedPremium;
        }
      });
    });

    if (!premium) {
      return null;
    }

    return (
      <div className="your-premium">
        Premium (inc {product.metadata.tax_type})
        <span className="selected-premium">
          <FormattedNumber
            value={premium.gross}
            style="currency"
            currency={premium.currency}
          />
        </span>
      </div>
    );
  }

  private saveQuote = () => {
    const { actions, change } = this.props;

    change('quote', 'createAccount', true);
    change('quote', 'generateDocuments', true);
    change('quote', 'emailSend', true);
    actions.quote.submitForm(null, true, true);
    this.setState({ showSaveQuoteModal: true });
  }

  private handleCloseSaveQuoteModal = () => {
    this.setState({ showSaveQuoteModal: false });
  }

  private handleGoToMyAccount = () => {
    const { actions, change } = this.props;
    actions.push('/account/summary');
  }

  private handleGoToCreateAnAccount = () => {
    const { actions, change } = this.props;
    actions.push('/auth/create-an-account');
  }

  private renderSaveQuote = () => {
    const { values, channel, complete } = this.props;

    if (complete) {
      return null;
    }

    if (values.quoteReference) {
      return (
        <div className="section">
          <Button bsStyle="primary" block={true} onClick={this.saveQuote}>
            Save Quote
          </Button>
          <Modal show={this.state.showSaveQuoteModal} onHide={this.handleCloseSaveQuoteModal}>
            <Modal.Header closeButton={true}>
              <Modal.Title>Your quote has been saved</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <h3 className="margin-bottom">Your quote reference is: {values.quoteReference}</h3>
              <p>
                If you would like to retrieve your quote please create an account.
                If you already have an account then please login.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                bsStyle="primary"
                onClick={this.handleGoToMyAccount}
              >
                Go to My Account
              </Button>
              <Button
                bsStyle="success"
                onClick={this.handleGoToCreateAnAccount}
              >
                Create an account
              </Button>
              <Button onClick={this.handleCloseSaveQuoteModal}>Close</Button>
            </Modal.Footer>
          </Modal>
        </div>
      );
    }
  }

  public render() {
    const { values, product, complete } = this.props;

    if (!values || !product) {
      return null;
    }

    return (
      <div className="quote-summary">
        <Row>
          <Col sm={12}>
            <h4>{complete ? 'Your Policy Summary' : 'Your Quote Summary'}</h4>

            {this.renderPremium()}
            {this.renderTripType()}
            {this.renderTravelDate()}
            {this.renderDestination()}
            {this.renderAdditionalOptions()}
            {this.renderActivities()}
            {this.renderTravellers()}
            {this.renderSaveQuote()}
            {this.props.channel.key === 'salt-om-ls-b2c' || this.props.channel.key === 'salt-om-b2c'
              ? this.renderSchemeDocument('Insurance Product Information Document')
              : null
            }
            {this.props.channel.key === 'salt-om-ls-b2c' || this.props.channel.key === 'salt-om-b2c'
              ? this.renderSchemeDocument('Policy Wording')
              : null
            }
            {/*{this.props.channel.key === 'salt-om-ls-b2c' || this.props.channel.key === 'salt-om-b2c'*/}
            {/*  ? this.renderProductDocument('Information: Lloyds Salt OM War Cover', 'War Cover')*/}
            {/*  : null*/}
            {/*}*/}
          </Col>
        </Row>
      </div>
    );
  }
}

let selector;

export default flow([
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const { quotes, complete } = state.quote;
      const values = selector(
        state,
        'destinations',
        'startDate',
        'endDate',
        'region',
        'tripType',
        'options',
        'travellers',
        'groupType',
        'schemeId',
        'quoteReference',
        'activities',
      );

      return {
        i18n: state.branding.i18n,
        channel: state.branding.channel,
        policyApplication: state.quote.application,
        destinations: state.quote.destinations,
        values,
        quotes,
        complete,
      };
    },
    (dispatch) => ({
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        push: bindActionCreators(push, dispatch),
      },
      change: bindActionCreators(change, dispatch),
    }),
  ),
])(QuoteSummary);
